// « Long lists get their own frame; the page never scrolls » (owner, 2026-10-07).
//
// A frame that runs from where it starts down to the bottom of the window, so
// each column inside it scrolls on its own and the page itself stays still.
// Returns a callback ref and the height to give the frame (null until measured,
// or when off — a phone lays out its own full screen).
import { useCallback, useEffect, useState } from 'react';

/** The frame's height for its top edge: down to the window bottom, never under `min`. */
export function frameHeight(top: number, viewport: number, bottomGap = 24, min = 480): number {
  return Math.max(min, Math.round(viewport - top - bottomGap));
}

export function useFillHeight(on = true): [(el: HTMLElement | null) => void, number | null] {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [h, setH] = useState<number | null>(null);
  const ref = useCallback((node: HTMLElement | null) => setEl(node), []);
  useEffect(() => {
    if (!on || !el || typeof window === 'undefined') { setH(null); return; }
    const measure = () => {
      // The top relative to the PAGE, so a scrolled window does not grow the frame.
      const top = el.getBoundingClientRect().top + window.scrollY;
      setH(frameHeight(top, window.innerHeight));
    };
    measure();
    window.addEventListener('resize', measure);
    // Banners above (system alert, numbers strip) can change height after load.
    const ro = typeof ResizeObserver !== 'undefined' && el.parentElement ? new ResizeObserver(measure) : null;
    if (ro && el.parentElement) ro.observe(el.parentElement);
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [on, el]);
  return [ref, h];
}
